import React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { getProjectBillingAccess } from "../../utils/projectBillingAccess";
import { billingDaysPhase, billingJourneyMessage, billingJourneyState } from "../../utils/billingJourney";
import { trackBillingJourney } from "../../utils/billingJourneyTelemetry";

function dateLabel(value) {
  if (!value) return "la fecha indicada";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "la fecha indicada";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "long" }).format(date);
}

export default function BillingAccessBanner({ project }) {
  const navigate = useNavigate();
  const location = useLocation();
  const access = getProjectBillingAccess(project);
  const projectId = project?.slug || project?.id || project?._id;

  if (!projectId) return null;

  const billingPath = `/dashboard/proyectos/${projectId}/configuracion/suscripcion`;
  const alreadyInBilling = location.pathname.includes("/configuracion/suscripcion");
  if (alreadyInBilling) return null;

  const visitBilling = (kind, guide = false) => {
    trackBillingJourney(project, "billing_conversion_cta_clicked", {
      source: "trial_banner", state: billingJourneyState(access), actionKind: kind,
    });
    navigate(`${billingPath}${guide ? "?guia=modulos" : ""}`);
  };

  // La prueba es gratuita de verdad: mostrar el aprendizaje comercial por etapas,
  // sin una alerta a diario, sin cuenta regresiva falsa y sin bloquear operaciones.
  if (access.isTrial && !access.trialExpired) {
    const phase = billingDaysPhase(access);
    if (!phase || phase === "explore") return null;
    const message = billingJourneyMessage(access);
    return (
      <Alert severity={phase === "last_day" || phase === "last_three_days" ? "warning" : "info"}
        sx={{ mb: 2, borderRadius: 3, "& .MuiAlert-message": { width: "100%" } }}>
        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} justifyContent="space-between" spacing={1.3}>
          <Box>
            <AlertTitle sx={{ mb: 0.2, fontWeight: 950 }}>{message.title}</AlertTitle>
            <Typography variant="body2">Aprendé qué herramientas usaste durante la prueba y elegí cuáles te conviene conservar. Podés empezar con pocos módulos y ampliar más adelante.</Typography>
          </Box>
          <Button variant="outlined" onClick={() => visitBilling("trial_guide", true)} sx={{ textTransform: "none", fontWeight: 900, flexShrink: 0 }}>Ver módulos y precios</Button>
        </Stack>
      </Alert>
    );
  }

  const paymentSuspended = access.reason === "billing_suspended";
  if (!access.isGrace && !access.isLandingOnly && !paymentSuspended) return null;

  if (access.isGrace) {
    const daysText = access.daysRemaining === 1
      ? "Queda 1 día"
      : `Quedan ${access.daysRemaining ?? "pocos"} días`;

    return (
      <Alert
        severity="warning"
        icon={<WarningAmberRoundedIcon />}
        sx={{
          mb: 3,
          borderRadius: 3,
          border: "1px solid rgba(237,108,2,.25)",
          alignItems: "center",
          "& .MuiAlert-message": { width: "100%" },
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ md: "center" }}
          spacing={2}
        >
          <Box>
            <AlertTitle sx={{ fontWeight: 950, mb: 0.25 }}>
              No pudimos cobrar la última mensualidad
            </AlertTitle>
            <Typography variant="body2">
              Tu sistema sigue funcionando normalmente hasta el {dateLabel(access.graceUntil)}. {daysText} para actualizar la tarjeta sin interrupciones.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="warning"
            startIcon={<CreditCardRoundedIcon />}
            onClick={() => visitBilling("resolve_payment")}
            sx={{ borderRadius: 999, textTransform: "none", fontWeight: 950, flexShrink: 0 }}
          >
            Resolver pago
          </Button>
        </Stack>
      </Alert>
    );
  }

  const trialEnded = access.reason === "trial_expired";

  return (
    <Alert
      severity="error"
      icon={<LockClockRoundedIcon />}
      sx={{
        mb: 3,
        borderRadius: 3,
        border: "1px solid rgba(211,47,47,.22)",
        alignItems: "center",
        "& .MuiAlert-message": { width: "100%" },
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ md: "center" }}
        spacing={2}
      >
        <Box>
          <AlertTitle sx={{ fontWeight: 950, mb: 0.25 }}>
            {trialEnded
              ? "Tu prueba gratis completa de 30 días terminó"
              : paymentSuspended
                ? "El proyecto está suspendido por pago"
                : "Las funciones operativas están temporalmente pausadas"}
          </AlertTitle>
          <Typography variant="body2">
            {trialEnded
              ? "La landing continúa visible y tus datos están seguros. Elegí los servicios que quieras conservar para reactivar productos, pedidos, cobros, estadísticas y sistema."
              : paymentSuspended
                ? "Tus datos están seguros. Regularizá el pago para recuperar el acceso al proyecto y sus herramientas."
                : "La landing continúa visible y tus datos están seguros. Regularizá el pago para reactivar automáticamente productos, pedidos, cobros, estadísticas y sistema."}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="error"
          startIcon={<CreditCardRoundedIcon />}
          onClick={() => visitBilling(trialEnded ? "trial_reactivation" : "resolve_payment", trialEnded)}
          sx={{ borderRadius: 999, textTransform: "none", fontWeight: 950, flexShrink: 0 }}
        >
          {trialEnded ? "Elegir suscripción" : paymentSuspended ? "Resolver pago" : "Reactivar ahora"}
        </Button>
      </Stack>
    </Alert>
  );
}
